/**
 * ONE VIDEO FILE, FROM THE AUTHOR'S DISK TO THE DRAFT (v1.0.29) - the picture upload's two legs
 * (../articles/editor/use-article-image-upload.ts), for a file up to 3 GB.
 *
 * Leg one writes the bytes into the caller's own quarantine prefix through the editor's one
 * `QuarantineUploader`, NAMING THE TYPE: the storage rules admit a video only as `video/mp4` or
 * `video/quicktime`, and a browser may report no type for a `.mov`, so it is read off the name then.
 * Leg two is `setArticleVideo` naming that object; the media worker then checks and converts it.
 *
 * Before a byte moves the type and the size are checked, and then the length and the picture as the
 * browser reads them (./video-metadata.ts), against the media worker's own limits - so a file the
 * worker would refuse after the whole upload is refused at once, in the worker's words. What the
 * browser cannot read is the worker's to measure. The write resumes across a dropped connection
 * only while the page stays open, so the screen says so, holds Send and warns before leaving while
 * it runs.
 *
 * ★ BYTES THAT WENT IN ARE NOT SENT TWICE. When leg two is refused after leg one finished, the
 * intake is kept and `retryAttach` names it again: a gigabyte is never uploaded again for one failed
 * call. Choosing another file, Cancel or leaving drops it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { VIDEO_MAX_BYTES, useEditorApi } from '../../features/cabinet/api/editor-api';
import type { ArticleVideo } from '../../features/cabinet/api/editor-api';
import { useEditorUploader } from '../articles/editor/use-article-image-upload';
import { useUploadsInFlight } from '../articles/editor/uploads-in-flight';
import { judgeVideoMetadata, useVideoMetadataReader } from './video-metadata';
import type { VideoShapeRefusal } from './video-metadata';

/** What the file picker offers: MP4 and MOV, by type and by name. */
export const VIDEO_ACCEPT = 'video/mp4,video/quicktime,.mp4,.mov';

/** `ArticleVideoRequest.fileName`: 1 to 200 characters. */
const FILE_NAME_MAX = 200;

export type VideoUploadState =
  | { readonly phase: 'idle' }
  /** Reading the file's length and shape, then writing the bytes. `progress` is 0…1 of `byteSize`. */
  | {
      readonly phase: 'uploading';
      readonly fileName: string;
      readonly byteSize: number;
      readonly progress: number;
    }
  /** The bytes are in; `setArticleVideo` is naming them. */
  | { readonly phase: 'attaching'; readonly fileName: string }
  /** The bytes are in, and `setArticleVideo` was refused: `retryAttach` names them again. */
  | { readonly phase: 'attachFailed'; readonly fileName: string; readonly error: unknown }
  | { readonly phase: 'failed'; readonly error: unknown };

/**
 * Refused here, before the writer is asked for anything: by its type or size, or by what the
 * browser read of it, under the media worker's own code (`failureCode`).
 */
export class VideoFileRefused extends Error {
  override readonly name = 'VideoFileRefused';
  constructor(readonly reason: 'type' | 'size' | 'empty' | VideoShapeRefusal) {
    super(`The file was refused before upload: ${reason}.`);
  }
}

/** The stored type: the browser's when it is one of the two, else the name's. */
export function videoContentType(file: File): 'video/mp4' | 'video/quicktime' | null {
  if (file.type === 'video/mp4' || file.type === 'video/quicktime') {
    return file.type;
  }
  const name = file.name.toLowerCase();
  if (name.endsWith('.mp4')) {
    return 'video/mp4';
  }
  if (name.endsWith('.mov')) {
    return 'video/quicktime';
  }
  return null;
}

export interface VideoUpload {
  readonly state: VideoUploadState;
  /** Resolves with the attached video, or `undefined` when it was refused or cancelled. */
  readonly upload: (file: File) => Promise<ArticleVideo | undefined>;
  /** After `attachFailed`: `setArticleVideo` again, with the same intake and no upload. */
  readonly retryAttach: () => Promise<ArticleVideo | undefined>;
  readonly cancel: () => void;
  readonly dismiss: () => void;
}

const IDLE: VideoUploadState = { phase: 'idle' };

/** What `setArticleVideo` names: the object leg one wrote. */
interface Uploaded {
  readonly intakeId: string;
  readonly fileName: string;
  readonly byteSize: number;
}

export function useVideoUpload(articleId: string): VideoUpload {
  const api = useEditorApi();
  const uploader = useEditorUploader();
  const uploads = useUploadsInFlight();
  const readMetadata = useVideoMetadataReader();
  const [state, setState] = useState<VideoUploadState>(IDLE);
  const inFlight = useRef<AbortController | null>(null);
  // Kept while `attachFailed`, for `retryAttach`.
  const uploaded = useRef<Uploaded | null>(null);

  useEffect(
    () => () => {
      // Leaving the screen stops the write: nothing is left to attach it to.
      inFlight.current?.abort();
      inFlight.current = null;
    },
    [],
  );

  // Counted in while it goes in, so Send waits (the picture uploads' own count).
  const goingIn = state.phase === 'uploading' || state.phase === 'attaching';
  useEffect(() => (goingIn ? uploads.hold() : undefined), [goingIn, uploads]);

  // Leg two, under the controller in flight; a refusal keeps `done` for `retryAttach`.
  const attach = useCallback(
    async (done: Uploaded, controller: AbortController): Promise<ArticleVideo | undefined> => {
      const current = (): boolean => inFlight.current === controller;
      setState({ phase: 'attaching', fileName: done.fileName });
      try {
        const video = await api.setArticleVideo(articleId, {
          source: 'upload',
          intakeId: done.intakeId,
          fileName: done.fileName,
          byteSize: done.byteSize,
        });
        if (!current()) {
          return undefined;
        }
        inFlight.current = null;
        setState(IDLE);
        return video;
      } catch (cause) {
        if (!current()) {
          return undefined;
        }
        inFlight.current = null;
        uploaded.current = done;
        setState({ phase: 'attachFailed', fileName: done.fileName, error: cause });
        return undefined;
      }
    },
    [api, articleId],
  );

  const upload = useCallback(
    async (file: File): Promise<ArticleVideo | undefined> => {
      inFlight.current?.abort();
      inFlight.current = null;
      uploaded.current = null;
      const contentType = videoContentType(file);
      const refused =
        contentType === null
          ? 'type'
          : file.size === 0
            ? 'empty'
            : file.size >= VIDEO_MAX_BYTES
              ? 'size'
              : null;
      if (refused !== null || contentType === null) {
        setState({ phase: 'failed', error: new VideoFileRefused(refused ?? 'type') });
        return undefined;
      }
      const controller = new AbortController();
      inFlight.current = controller;
      const current = (): boolean => inFlight.current === controller;
      const intakeId = crypto.randomUUID();
      const fileName = file.name.length === 0 ? 'video' : file.name.slice(0, FILE_NAME_MAX);
      setState({ phase: 'uploading', fileName, byteSize: file.size, progress: 0 });
      try {
        // A reader that throws has read nothing: the worker decides then.
        const metadata = await readMetadata(file).catch(() => null);
        if (!current()) {
          return undefined;
        }
        const shape = metadata === null ? null : judgeVideoMetadata(metadata);
        if (shape !== null) {
          inFlight.current = null;
          setState({ phase: 'failed', error: new VideoFileRefused(shape) });
          return undefined;
        }
        await uploader.putQuarantineObject(
          intakeId,
          file,
          (fraction) => {
            if (current()) {
              const progress = Number.isFinite(fraction) ? Math.min(Math.max(fraction, 0), 1) : 0;
              setState({ phase: 'uploading', fileName, byteSize: file.size, progress });
            }
          },
          { signal: controller.signal, contentType },
        );
      } catch (cause) {
        // A cancel lands here too, and is not a failure: whoever cancelled has moved on.
        if (!current()) {
          return undefined;
        }
        inFlight.current = null;
        setState({ phase: 'failed', error: cause });
        return undefined;
      }
      if (!current()) {
        return undefined;
      }
      return attach({ intakeId, fileName, byteSize: file.size }, controller);
    },
    [uploader, readMetadata, attach],
  );

  const retryAttach = useCallback(async (): Promise<ArticleVideo | undefined> => {
    const done = uploaded.current;
    if (done === null || inFlight.current !== null) {
      return undefined;
    }
    uploaded.current = null;
    const controller = new AbortController();
    inFlight.current = controller;
    return attach(done, controller);
  }, [attach]);

  const cancel = useCallback(() => {
    inFlight.current?.abort();
    inFlight.current = null;
    uploaded.current = null;
    setState(IDLE);
  }, []);

  const dismiss = useCallback(() => {
    uploaded.current = null;
    setState((current) =>
      current.phase === 'failed' || current.phase === 'attachFailed' ? IDLE : current,
    );
  }, []);

  return { state, upload, retryAttach, cancel, dismiss };
}
